// ============================================
// pages/reports/ReportsHub.tsx
// Landing page at /reports — index of the 5 MD/CAO report families.
// ============================================
import React from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, History, Wallet, Users, FileSpreadsheet,
  ChevronRight, BarChart3,
} from 'lucide-react';

interface ReportCard {
  title: string;
  description: string;
  to: string;
  icon: React.ReactNode;
  color: string;
  source: string;
}

const REPORTS: ReportCard[] = [
  {
    title: '1. Daily Progress Report (DPR)',
    description: 'Per-commodity DPR with proforma header (sown area, MSP, yield, expected production, market rate) and district-wise procurement breakdown.',
    to: '/reports/dpr',
    icon: <FileText className="w-6 h-6" />,
    color: 'from-blue-500 to-blue-600',
    source: 'MARKFED DPR report .xlsx → per-commodity sheets',
  },
  {
    title: '2. Multi-Year Procurement Summary',
    description: 'Year × Crop × MSP × farmers × qty × value rollup with year totals — historical view since 2014-15.',
    to: '/reports/historical',
    icon: <History className="w-6 h-6" />,
    color: 'from-purple-500 to-purple-600',
    source: 'MARKFED DPR report .xlsx → MARKFED 1 sheet',
  },
  {
    title: '3. MD Loan & Funds Flow',
    description: 'GO ref → bank → sanctioned → drawn → transferred to district → UTR → transferred to farmers. The credit-first chain MD watches.',
    to: '/reports/md-sheet',
    icon: <Wallet className="w-6 h-6" />,
    color: 'from-emerald-500 to-emerald-600',
    source: 'WORKSHEETS - MD SIR.xlsx → MD sheet',
  },
  {
    title: '4. District Farmer Payment Tracker',
    description: 'PC × PACS × farmers × qty × cost × amount received × payment released × balance — the partial-payment ledger.',
    to: '/reports/farmers-sheet',
    icon: <Users className="w-6 h-6" />,
    color: 'from-orange-500 to-orange-600',
    source: 'WORKSHEETS - MD SIR.xlsx → DISTRICT FARMERS sheet',
  },
  {
    title: '5. Operational Proformas',
    description: 'Read-only view of unloading bills, transport bills, and gunny utilization — the 3 sub-reports CAO/MD certify before payment release.',
    to: '/reports/proformas',
    icon: <FileSpreadsheet className="w-6 h-6" />,
    color: 'from-amber-500 to-amber-600',
    source: 'procurement proformas.xlsx → unloading / transport / gunnies',
  },
];

const SECONDARY: ReportCard[] = [
  {
    title: 'Payables Ledger',
    description: 'Every outstanding balance across farmer payments, transport, unloading, gunny, and storage — with aging buckets (current / pending / overdue). Surfaces the credit-first reality of procurement.',
    to: '/reports/payables',
    icon: <Wallet className="w-6 h-6" />,
    color: 'from-orange-500 to-orange-600',
    source: 'all 5 payable sources + installment ledgers',
  },
  {
    title: 'Utilization Reconciliation',
    description: 'Per-district received vs spent. 5 heads live-computed from proforma ledgers (farmer payments, gunnies, transport, unloading, storage) + 6 DM-entered heads. Flags over-utilization.',
    to: '/reports/utilization-reconciliation',
    icon: <BarChart3 className="w-6 h-6" />,
    color: 'from-cyan-500 to-cyan-600',
    source: 'reconciliation/utilization endpoint',
  },
  {
    title: 'Reconciliation Dashboard',
    description: 'Variance reconciliation across production, procurement, and stocked quantities by commodity.',
    to: '/reports/reconciliation',
    icon: <BarChart3 className="w-6 h-6" />,
    color: 'from-rose-500 to-rose-600',
    source: 'reconciliation module',
  },
  {
    title: 'Import / Export',
    description: 'Bulk Excel import for procurement data; export DPR and consolidated reports as Excel/PDF.',
    to: '/reports/import-export',
    icon: <FileSpreadsheet className="w-6 h-6" />,
    color: 'from-slate-500 to-slate-600',
    source: 'export & import modules',
  },
];

const ReportTile: React.FC<{ r: ReportCard }> = ({ r }) => (
  <Link
    to={r.to}
    className="group bg-white rounded-xl shadow-sm border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all p-5 flex flex-col"
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${r.color} text-white flex items-center justify-center shadow-md`}>
        {r.icon}
      </div>
      <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all" />
    </div>
    <h3 className="text-base font-bold text-gray-900 mb-1.5">{r.title}</h3>
    <p className="text-xs text-gray-600 mb-3 flex-1">{r.description}</p>
    <p className="text-[10px] text-gray-400 italic border-t border-gray-100 pt-2">Source: {r.source}</p>
  </Link>
);

export const ReportsHub: React.FC = () => {
  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <BarChart3 className="w-7 h-7 text-blue-600" />
          MD Reports
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          The 5 report families MD/CAO use end-of-day to monitor procurement, payments, and operations.
        </p>
      </div>

      <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
        Primary Reports
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {REPORTS.map((r) => (<ReportTile key={r.to} r={r} />))}
      </div>

      <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
        Other
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SECONDARY.map((r) => (<ReportTile key={r.to} r={r} />))}
      </div>
    </div>
  );
};

export default ReportsHub;
